import { STORE_ORIGIN } from '../config';
import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const BASE_URL = STORE_ORIGIN;
const PRODUCT_IMAGES_URL = `${BASE_URL}/admin/uploads/products`;

const SubcategoryProductPage = () => {
  const { mainSlug, categorySlug, subcategorySlug } = useParams();
  const [products, setProducts] = useState([]);
  const [subcategoryInfo, setSubcategoryInfo] = useState(null);
  const [categoryInfo, setCategoryInfo] = useState(null);
  const [mainCategoryInfo, setMainCategoryInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to extract filename from path
  const extractFilename = (path) => {
    if (!path) return null;
    const cleanPath = path.split('?')[0];
    return cleanPath.split('/').pop();
  };

  useEffect(() => {
    const fetchSubcategory = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(`${BASE_URL}/api/api.php`);
        const data = res.data;

        console.log("API Data for debugging:", data); // Debug log

        const mainCategory = data.find(mc => mc.slug === mainSlug);
        if (!mainCategory) throw new Error("Main category not found");
        setMainCategoryInfo(mainCategory);

        const category = mainCategory.categories.find(cat => cat.slug === categorySlug);
        if (!category) throw new Error("Category not found");
        setCategoryInfo(category);

        const subcategory = category.subcategories.find(sub => sub.slug === subcategorySlug);
        if (!subcategory) throw new Error("Subcategory not found");
        setSubcategoryInfo({ 
          id: subcategory.id, 
          name: subcategory.name,
          slug: subcategory.slug 
        });

        if (!subcategory.products || subcategory.products.length === 0) {
          setError("No products found in this subcategory");
          setLoading(false);
          return;
        }

        // Map products with proper image URLs - FIXED
        const mappedProducts = subcategory.products.map(p => {
          const productImage = p.main_image || p.image;
          
          console.log("Product image debug:", {
            productName: p.name,
            productImage: productImage,
            extracted: extractFilename(productImage)
          }); // Debug log

          let image;
          if (productImage) {
            const filename = extractFilename(productImage);
            image = `${PRODUCT_IMAGES_URL}/${filename}`;
          } else {
            image = "https://via.placeholder.com/500x500?text=Product+Image";
          }

          const productSlug = p.slug || p.name.toLowerCase().replace(/\s+/g, '-');

          return {
            ...p,
            product_id: p.id,
            product_name: p.name,
            slug: productSlug,
            image: image,
            base_price: p.base_price || p.price || 0,
            description: p.description || "",
            rating: p.rating || (Math.random() * 2 + 3).toFixed(1),
            reviews: p.reviews || Math.floor(Math.random() * 50),
            stock_quantity: p.stock_quantity || Math.floor(Math.random() * 100),
          };
        });

        console.log("Mapped products with images:", mappedProducts); // Debug log
        setProducts(mappedProducts);
        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load products");
        setLoading(false);
      }
    };

    fetchSubcategory();
  }, [mainSlug, categorySlug, subcategorySlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto p-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-red-600 text-xl mb-4">⚠️ {error}</div>
            <Link 
              to={`/${mainSlug}/${categorySlug}`}
              className="inline-block px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
            >
              ← Back to {categoryInfo?.name}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-3">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-red-600 transition">
              Home
            </Link>
            <span className="mx-2">/</span>
            {/* Non-clickable main category */}
            <span className="text-gray-500">
              {mainCategoryInfo?.name}
            </span>
            <span className="mx-2">/</span>
            {/* Back to category link */}
            <Link 
              to={`/${mainSlug}/${categorySlug}`} 
              className="hover:text-red-600 transition"
            >
              {categoryInfo?.name}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-semibold">
              {subcategoryInfo?.name}
            </span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <div className="mb-4">
          <Link 
            to={`/${mainSlug}/${categorySlug}`}
            className="inline-flex items-center text-sm text-red-600 hover:text-red-700 mb-2"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to {categoryInfo?.name}
          </Link>
        </div>
        
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          {subcategoryInfo?.name}
        </h1>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <p className="text-gray-600">
            Browse our collection of {subcategoryInfo?.name.toLowerCase()} products
          </p>
          <div className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
            {products.length} {products.length === 1 ? 'product' : 'products'} available
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 pb-12">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-gray-500 text-lg mb-4">
              No products found in this subcategory
            </div>
            <Link 
              to={`/${mainSlug}/${categorySlug}`}
              className="text-red-600 hover:text-red-700 font-semibold"
            >
              ← Back to {categoryInfo?.name}
            </Link>
          </div>
        ) : (
          <>
            {/* Optional: Sort/Filter Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-700">Sort by:</span>
                <select className="border rounded px-3 py-1 text-sm">
                  <option>Featured</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Newest</option>
                  <option>Best Selling</option>
                </select>
              </div>
              <div className="text-sm text-gray-600">
                Showing all {products.length} products
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {products.map(product => (
                <Link
                  key={product.product_id}
                  to={`/product/${product.slug}`}
                  className="group bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                >
                  <div className="relative pt-[100%] bg-gray-100">
                    <img
                      src={product.image}
                      alt={product.product_name}
                      className="absolute inset-0 w-full h-full object-contain p-4 transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        console.error('Image failed to load:', {
                          product: product.product_name,
                          url: product.image,
                          productData: product
                        });
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/300x300?text=Product+Image";
                      }}
                      onLoad={() => {
                        console.log('Image loaded successfully:', product.product_name);
                      }}
                    />
                    
                    {/* Stock indicator */}
                    {product.stock_quantity > 0 ? (
                      <div className="absolute top-3 left-3 bg-green-600 text-white text-xs px-2 py-1 rounded-full">
                        In Stock
                      </div>
                    ) : (
                      <div className="absolute top-3 left-3 bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                        Out of Stock
                      </div>
                    )}
                    
                    {/* Quick View Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="bg-white text-gray-900 px-4 py-2 rounded font-semibold text-sm">
                        Quick View
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 group-hover:text-red-600 line-clamp-2 mb-2 h-12">
                      {product.product_name}
                    </h3>
                    
                    {/* Rating */}
                    <div className="flex items-center mb-2">
                      <div className="flex text-yellow-400">
                        {[...Array(5)].map((_, i) => (
                          <span key={i}>
                            {i < Math.floor(product.rating) ? '★' : '☆'}
                          </span>
                        ))}
                      </div>
                      <span className="text-sm text-gray-600 ml-2">
                        ({product.reviews})
                      </span>
                    </div>
                    
                    {/* Price */}
                    <div className="flex justify-between items-center">
                      <p className="text-red-600 font-bold text-lg">
                        QAR {product.base_price}
                      </p>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {categoryInfo?.name}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Back to Category Link */}
            <div className="mt-12 text-center">
              <Link 
                to={`/${mainSlug}/${categorySlug}`}
                className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to {categoryInfo?.name}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubcategoryProductPage;